import React, { useEffect, useState } from 'react';
import Box, { BoxProps } from 'ui-box';
import { useDebouncedCallback } from 'use-debounce/lib';
import { Button, majorScale, minorScale, Spinner } from '.';
import { UserThumbnail } from '../components/settings/UserThumbnail';
import {
  IContactSnapshotOut,
  ITeamMember,
  ITeamMemberSnapshotOut,
  useStores,
} from '../stores';
import { IPaginationFilter } from '../stores/utils/modelWithCRUD';
import { ControlGroup, EmptyInputGroup } from './InputGroup';
import { ISelectProps, Select } from './Select';

export interface IUserSelectProps extends ISelectProps<ITeamMember> {
  filter?: IPaginationFilter<IContactSnapshotOut>['filter'];
}

export const UserSelect: React.FC<IUserSelectProps> = ({
  children,
  query: propQuery = '',
  placeholder = 'Vælg en bruger',
  title,
  selected = null,
  onSelect,
  disabled = false,
  onOpen,
  filter = {},
  ...props
}) => {
  const { teamMemberStore } = useStores();
  const [opened, setOpened] = useState(false);
  const [innerSelected, setInnerSelected] = useState<ITeamMember | null>(null);

  useEffect(() => {
    let isSub = true;
    if (selected) {
      teamMemberStore
        .getItem(selected)
        .then((user) => isSub && setInnerSelected(user));
    }
    return () => {
      isSub = false;
    };
  }, [teamMemberStore, selected]);

  const [items, setItems] = useState<ITeamMember[]>();
  const [query, setQuery] = useState(propQuery);
  const debouncedQuery = useDebouncedCallback(setQuery, 150);

  useEffect(() => {
    debouncedQuery.callback(propQuery);
  }, [debouncedQuery, propQuery]);

  useEffect(() => {
    if (!opened) return;
    let isSub = true;
    teamMemberStore
      .fetchMore({ filter: { ...filter, search: query } })
      .then((res) => isSub && setItems(res.items));

    return () => {
      isSub = false;
    };
  }, [teamMemberStore, filter, query, opened]);

  return (
    <Select
      {...props}
      title={title}
      placeholder={placeholder}
      disabled={disabled}
      isOpen={opened}
      onOpen={() => {
        setOpened(true);
        onOpen && onOpen();
      }}
      onClose={() => {
        setOpened(false);
      }}
      onQueryChange={debouncedQuery.callback}
      items={items}
      isLoading={!items || teamMemberStore.isLoading}
      resolveSelectedValue={(id) => teamMemberStore.getItem(id)}
      itemRenderer={(item) => (
        <>
          <UserThumbnail
            user={item.user}
            marginRight={majorScale(1)}
            showName
          />
        </>
      )}
      onSelect={(contact) => {
        setInnerSelected(contact);
        onSelect && onSelect(contact);
      }}
      value={innerSelected ? innerSelected.id : undefined}
      valueRenderer={(selected) => selected.user.name}
    >
      {children}
    </Select>
  );
};

export interface IUsersInputprops
  extends Omit<
    Omit<Omit<BoxProps<'div'>, 'onChange'>, 'onBlur'>,
    'defaultValue'
  > {
  value?: ITeamMemberSnapshotOut['id'][];
  defaultValue?: ITeamMemberSnapshotOut['id'][];
  onChange?: (users: ITeamMember[]) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  filter?: IPaginationFilter<ITeamMemberSnapshotOut>['filter'];
}

export const UsersInput: React.FC<IUsersInputprops> = ({
  placeholder = 'Tilføj ansvarlig',
  value,
  defaultValue = [],
  onChange,
  onBlur,
  filter = {},
  ...props
}) => {
  const { teamMemberStore } = useStores();
  const [members, setMembers] = useState<(ITeamMember | undefined)[]>([]);

  const [innerValue, setInnerValue] =
    useState<ITeamMemberSnapshotOut['id'][]>(defaultValue);

  useEffect(() => {
    setInnerValue(value || []);
  }, [value]);

  useEffect(() => {
    let isSub = true;
    if (innerValue && innerValue.length > 0) {
      teamMemberStore
        .fetchMore({ filter: { id__in: innerValue } })
        .then((res) => isSub && setMembers(Array.from(res.items)));
    } else {
      setMembers([]);
    }
    return () => {
      isSub = false;
    };
  }, [innerValue, teamMemberStore]);

  const addUser = (user: ITeamMember) => {
    const index = members.findIndex((u) => u && u.id === user.id);
    if (index === -1) {
      const uu = Array.from(members);
      uu.push(user);
      setMembers(uu);
      onChange && onChange(uu.filter((u) => !!u) as ITeamMember[]);
    }
  };

  const removeUser = (index: number) => {
    if (members.length === 0) return;
    const newMembers = Array.from(members);
    newMembers.splice(index, 1);
    setMembers(newMembers);
    onChange && onChange(newMembers.filter((u) => !!u) as ITeamMember[]);
  };

  return (
    <Box marginBottom={majorScale(1)}>
      {members.map((member, i) =>
        member ? (
          <ControlGroup fill marginBottom={minorScale(1)} key={member.id}>
            <EmptyInputGroup width="100%">{member.user.name}</EmptyInputGroup>
            <Button
              height={32}
              iconLeft="cross"
              onClick={() => removeUser(i)}
            />
          </ControlGroup>
        ) : (
          <Spinner size={16} key={'index-' + i} />
        )
      )}
      {/** TODO: Is this 'id__exclude' correct? */}
      <UserSelect onSelect={addUser} filter={{ id__exclude: innerValue }}>
        <Button iconLeft="add">{placeholder}</Button>
      </UserSelect>
    </Box>
  );
};
